import { createSlice } from '@reduxjs/toolkit';

/**
 * Site-trial staff console slice (site-trial billing PHASE2-PLAN P2-8a / B16).
 *
 * Reads and acts on `/v5/:orgID/billing-admin/site-trial/*` (App Server contract:
 * docs/features/billing/site-trial-staff-console.md). ZEZAMII STAFF ONLY: the panel renders only
 * for `selectIsInternalBillingAdmin`, and the SERVER is authoritative (403 STB_STAFF_ROLE_REQUIRED).
 *
 * 🛑 `pendingAction.operationID` is minted ONCE per staff decision (when the confirmation opens) and
 * reused on every retry of that decision. That is what makes a retried click idempotent on the
 * server (`replayed: true`); minting a new one per click would turn a double-submit into two decisions.
 */
/** Refusals that mean the decision was made on a row that has since moved: the dialog closes. */
export const STALE_DECISION_CODES = Object.freeze(['STB_STAFF_VERSION_STALE', 'STB_STAFF_HISTORY_CHANGED']);

export const initialState = {
	control: null,
	controlLoading: false,

	catalog: null,
	catalogLoading: false,

	agreements: [],
	agreementsLoading: false,
	agreementFilter: { state: null },

	selectedAgreementID: null,
	detail: null,
	detailLoading: false,

	holds: [],
	holdsLoading: false,

	// One obligation's D35 view (billed period, repaired rows, drift), for the release confirmation.
	holdDetail: null,
	holdDetailLoading: false,

	sessions: [],
	sessionsLoading: false,

	// { obligationID, action, operationID, version } while a confirmation is open.
	pendingAction: null,
	acting: false,
	lastResult: null,
	// { errorCode, error, currentVersion? } from the last refused action.
	actionError: null,

	error: null
};

const slice = createSlice({
	name: 'siteTrialStaff',
	initialState,
	reducers: {
		loadControlState: (state) => {
			state.controlLoading = true;
			state.error = null;
		},
		loadControlStateSuccess: (state, { payload }) => {
			state.controlLoading = false;
			state.control = payload || null;
		},
		loadControlStateError: (state, { payload }) => {
			state.controlLoading = false;
			state.error = payload;
		},

		loadCatalog: (state) => {
			state.catalogLoading = true;
		},
		loadCatalogSuccess: (state, { payload }) => {
			state.catalogLoading = false;
			state.catalog = payload || null;
		},
		loadCatalogError: (state, { payload }) => {
			state.catalogLoading = false;
			state.error = payload;
		},

		loadAgreements: (state, { payload }) => {
			state.agreementsLoading = true;
			state.error = null;
			if (payload) state.agreementFilter = { ...state.agreementFilter, ...payload };
		},
		loadAgreementsSuccess: (state, { payload }) => {
			state.agreementsLoading = false;
			state.agreements = payload || [];
		},
		loadAgreementsError: (state, { payload }) => {
			state.agreementsLoading = false;
			state.error = payload;
		},

		loadAgreement: (state, { payload }) => {
			state.detailLoading = true;
			state.error = null;
			if (state.selectedAgreementID !== payload) state.detail = null;
			state.selectedAgreementID = payload;
		},
		loadAgreementSuccess: (state, { payload }) => {
			state.detailLoading = false;
			state.detail = payload || null;
		},
		loadAgreementError: (state, { payload }) => {
			state.detailLoading = false;
			state.error = payload;
		},
		closeAgreement: (state) => {
			state.selectedAgreementID = null;
			state.detail = null;
		},

		loadHolds: (state) => {
			state.holdsLoading = true;
			state.error = null;
		},
		loadHoldsSuccess: (state, { payload }) => {
			state.holdsLoading = false;
			state.holds = payload || [];
		},
		loadHoldsError: (state, { payload }) => {
			state.holdsLoading = false;
			state.error = payload;
		},

		loadHoldDetail: (state) => {
			state.holdDetailLoading = true;
			state.holdDetail = null;
		},
		loadHoldDetailSuccess: (state, { payload }) => {
			state.holdDetailLoading = false;
			state.holdDetail = payload || null;
		},
		loadHoldDetailError: (state, { payload }) => {
			state.holdDetailLoading = false;
			state.error = payload;
		},

		loadSessions: (state) => {
			state.sessionsLoading = true;
			state.error = null;
		},
		loadSessionsSuccess: (state, { payload }) => {
			state.sessionsLoading = false;
			state.sessions = payload || [];
		},
		loadSessionsError: (state, { payload }) => {
			state.sessionsLoading = false;
			state.error = payload;
		},

		/** Open a confirmation. The caller mints the operationID, once for this decision. */
		openAction: (state, { payload }) => {
			state.pendingAction = payload;
			state.actionError = null;
			state.lastResult = null;
		},
		cancelAction: (state) => {
			state.pendingAction = null;
			state.actionError = null;
			state.acting = false;
		},
		runAction: (state) => {
			state.acting = true;
			state.actionError = null;
		},
		runActionSuccess: (state, { payload }) => {
			state.acting = false;
			state.lastResult = payload || null;
			state.pendingAction = null;
		},
		runActionError: (state, { payload }) => {
			state.acting = false;
			state.actionError = payload || { errorCode: null, error: 'The action failed.' };
			// The row moved under the decision (stale version) or its reviewed history did (new repaired
			// rows). Either way the decision was made on a row that no longer exists: close the dialog and
			// drop its operationID. The staff member reopens it on the reloaded row, which mints a NEW
			// operationID for the new decision. Never re-arm the old one with the server's version.
			if (STALE_DECISION_CODES.includes(payload?.errorCode)) state.pendingAction = null;
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
		// Staff data is platform-wide, but the staff role is evaluated in the selected org, so a profile
		// switch can turn a 201 into a non-staff caller. Never keep the previous session's view.
		builder.addCase('auth/switchProfileSuccess', () => initialState);
	}
});

export const siteTrialStaffActions = slice.actions;
export default slice.reducer;
